/**
 * Bundle 68 Task B - Meta Custom Conversion creator modal.
 *
 * Opens from the Funnel Completeness health cards to build a
 * retargeting audience in one click. Shows preset library, live
 * audience-size estimate, and dispatches to
 * `POST /api/admin/analytics/custom-conversions/create` which then
 * calls Meta's Marketing API.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Target } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const CustomConversionModal = ({ open, onOpenChange, defaultPreset }) => {
  const [presets, setPresets] = useState([]);
  const [preset, setPreset] = useState(defaultPreset || 'warm_cart_abandoners');
  const [pixels, setPixels] = useState([]);
  const [pixelId, setPixelId] = useState('');
  const [adAccountId, setAdAccountId] = useState('');
  const [estimate, setEstimate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  // Bundle 69 - Lookalike controls.
  const [alsoLookalike, setAlsoLookalike] = useState(true);
  const [lookalikeCountry, setLookalikeCountry] = useState('US');
  const [lookalikeRatio, setLookalikeRatio] = useState(0.02);
  const [seedPreview, setSeedPreview] = useState(null);
  const [forceSmallSeed, setForceSmallSeed] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          '/admin/analytics/custom-conversions',
        );
        if (!cancelled) setPresets(data.presets || []);
      } catch (e) { /* silent */ }
      try {
        const { data } = await api.get('/admin/pixels');
        const metaOnly = (data.pixels || [])
          .filter((p) => p.provider === 'meta');
        if (cancelled) return;
        setPixels(metaOnly);
        setPixelId((prev) => prev || (metaOnly[0]?.pixel_id || ''));
        setAdAccountId((prev) => prev || (metaOnly[0]?.ad_account_id || ''));
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open || !preset) return;
    setLoading(true);
    setEstimate(null);
    (async () => {
      try {
        const { data } = await api.post(
          '/admin/analytics/custom-conversions/estimate',
          { preset },
        );
        setEstimate(data);
      } catch (e) {
        setEstimate({ error: e?.response?.data?.detail || 'estimate failed' });
      } finally {
        setLoading(false);
      }
    })();
  }, [open, preset]);

  // Bundle 69 - poll seed preview when the lookalike checkbox is on.
  useEffect(() => {
    if (!open || !alsoLookalike) { setSeedPreview(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          '/admin/analytics/custom-conversions/lookalike-seed-preview',
          { params: { days: 180 } },
        );
        if (!cancelled) setSeedPreview(data);
      } catch (e) {
        if (!cancelled) setSeedPreview({
          error: e?.response?.data?.detail || 'seed preview failed',
        });
      }
    })();
    return () => { cancelled = true; };
  }, [open, alsoLookalike]);

  const activePreset = presets.find((p) => p.key === preset);

  const create = async (dryRun = false) => {
    if (!pixelId) {
      toast.error('Select a Meta pixel first.');
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post(
        '/admin/analytics/custom-conversions/create',
        { preset, pixel_id: pixelId,
          ad_account_id: adAccountId || undefined,
          dry_run: dryRun,
          also_create_lookalike: alsoLookalike,
          lookalike_country: lookalikeCountry,
          lookalike_ratio: lookalikeRatio,
          lookalike_seed_days: 180,
          force_small_seed: forceSmallSeed },
      );
      if (dryRun) {
        toast.success('Dry-run OK. Payload is valid for Meta Marketing API.');
      } else if (data.success) {
        const la = data.lookalike || {};
        let msg = `Created in Meta as ${data.meta_custom_conversion_id}. `;
        if (la.attempted && la.success && la.lookalike_audience_id) {
          msg += `Lookalike ${la.lookalike_audience_id} queued (2% ${lookalikeCountry}). `;
        } else if (la.attempted && !la.success) {
          msg += `Custom Conversion OK; Lookalike failed: ${la.message}. `;
        } else if (alsoLookalike && !la.attempted) {
          msg += `Lookalike NOT created: ${la.message || 'seed too small'}. `;
        }
        msg += 'Open Meta Ads Manager > Audiences.';
        toast.success(msg);
        onOpenChange(false);
      } else {
        toast.error(
          `Meta rejected: HTTP ${data.meta_http_status}. `
          + JSON.stringify(data.meta_response).slice(0, 200),
        );
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Create failed.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="custom-conversion-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Create Meta retargeting audience
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <Label>Preset</Label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger data-testid="cc-preset-select">
                <SelectValue placeholder="Pick a preset" />
              </SelectTrigger>
              <SelectContent>
                {presets.map((p) => (
                  <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activePreset ? (
              <p className="mt-1 text-xs text-neutral-500">
                {activePreset.description}
              </p>
            ) : null}
          </div>
          <div>
            <Label>Meta pixel</Label>
            <Select value={pixelId} onValueChange={(v) => {
              setPixelId(v);
              const px = pixels.find((p) => p.pixel_id === v);
              setAdAccountId(px?.ad_account_id || '');
            }}>
              <SelectTrigger data-testid="cc-pixel-select">
                <SelectValue placeholder="Pick a Meta pixel" />
              </SelectTrigger>
              <SelectContent>
                {pixels.map((p) => (
                  <SelectItem key={p.id} value={p.pixel_id}>
                    {p.pixel_id} {p.ad_account_id ? `(${p.ad_account_id})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="cc-ad-account">Ad account id</Label>
            <Input
              id="cc-ad-account"
              data-testid="cc-ad-account-input"
              value={adAccountId}
              onChange={(e) => setAdAccountId(e.target.value)}
              placeholder="act_1234567890"
            />
            <p className="mt-1 text-xs text-neutral-500">
              Prefill from pixel config. Leave blank to use the stored value.
            </p>
          </div>
          <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs">
            <div className="font-semibold text-neutral-800 mb-1">
              Audience estimate
            </div>
            {loading ? (
              <div className="flex items-center gap-2 text-neutral-500">
                <Loader2 className="h-3 w-3 animate-spin" /> computing...
              </div>
            ) : estimate?.error ? (
              <div className="text-red-600">{estimate.error}</div>
            ) : estimate ? (
              <div data-testid="cc-audience-estimate">
                <div className="text-2xl font-bold text-neutral-900 tabular-nums">
                  {estimate.audience_size} users
                </div>
                <div className="text-neutral-500 mt-1">
                  {estimate.include_hits} fired {estimate.include_event}
                  {' '}in {estimate.include_days}d,
                  {' '}{estimate.exclude_hits} also completed
                  {' '}{estimate.exclude_event} (excluded).
                </div>
              </div>
            ) : null}
          </div>
          {/* Bundle 69 - Auto-Lookalike controls */}
          <div className="rounded-md border border-neutral-200 p-3 text-xs space-y-3" data-testid="cc-lookalike-block">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="cc-lookalike-checkbox"
                checked={alsoLookalike}
                onChange={(e) => setAlsoLookalike(e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <div className="font-semibold text-neutral-800">
                  Also create Lookalike audience
                </div>
                <div className="text-neutral-500">
                  Seeded from Purchase completers in the last 180d.
                  Ready in Meta 6-24h after creation.
                </div>
              </div>
            </label>
            {alsoLookalike ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Country</Label>
                  <Select value={lookalikeCountry} onValueChange={setLookalikeCountry}>
                    <SelectTrigger className="h-8" data-testid="cc-lookalike-country">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['US', 'CA', 'GB', 'AU', 'IN', 'AE', 'SG', 'DE', 'FR'].map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Ratio</Label>
                  <Select value={String(lookalikeRatio)} onValueChange={(v) => setLookalikeRatio(parseFloat(v))}>
                    <SelectTrigger className="h-8" data-testid="cc-lookalike-ratio">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0.01, 0.02, 0.03, 0.05, 0.10].map((r) => (
                        <SelectItem key={r} value={String(r)}>{(r * 100).toFixed(0)}%</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {seedPreview ? (
                  <div className="col-span-2 text-xs" data-testid="cc-lookalike-seed">
                    <div className="text-neutral-700">
                      Seed: <span className="font-semibold">{seedPreview.seed_size}</span>
                      {' '}Purchase completers with matched PII (180d).
                    </div>
                    {seedPreview.warning ? (
                      <div className="mt-1 rounded bg-yellow-50 border border-yellow-200 p-2 text-yellow-800">
                        {seedPreview.warning}
                        <label className="flex items-center gap-1 mt-1">
                          <input
                            type="checkbox"
                            data-testid="cc-force-small-seed"
                            checked={forceSmallSeed}
                            onChange={(e) => setForceSmallSeed(e.target.checked)}
                          />
                          <span>Attempt anyway (Meta may reject)</span>
                        </label>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => create(true)}
            disabled={creating || !pixelId}
            data-testid="cc-dry-run-btn"
          >
            Preview payload (dry run)
          </Button>
          <Button
            onClick={() => create(false)}
            disabled={creating || !pixelId}
            data-testid="cc-create-btn"
          >
            {creating ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Creating...</>
            ) : 'Create in Meta'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomConversionModal;
